import "./Navbar.scss";

import { Avatar } from "@appkit4/react-components";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { FiCalendar, FiTerminal } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";

import { invalidateAiInsightJobs } from "../../../../api/aiInsights";
import {
  useCurrentUserProfile,
  userAccessQueryKeys,
  useUpdateUserAccess,
  useUserAccessPersonas,
  useUserAccessPlatformRoles,
} from "../../../../api/userAccess";
import logoImage from "../../../../assets/images/logo.png";
import { env } from "../../../../config/env";
import { useAuth } from "../../../../contexts/useAuth";
import { useDateOverride } from "../../../../contexts/useDateOverride";
import { AppRoutes } from "../../../../types/enums/appRoutes";
import { AriaLabel } from "../../../../types/enums/ariaLabels";
import { BrandLabel, ProfileMenuLabel } from "../../../../types/enums/labels";

const appVersion = import.meta.env.VITE_APP_VERSION;

const OVERRIDE_FIELDS: { key: "day1Date" | "signingDate" | "targetCompletionDate"; label: string }[] = [
  { key: "day1Date", label: "Day 1 Date" },
  { key: "signingDate", label: "Signing Date" },
  { key: "targetCompletionDate", label: "Target Completion" },
];

interface NavbarProps {
  onProfileMenuOpen: () => void;
}

export function Navbar({ onProfileMenuOpen }: NavbarProps) {
  const navigate = useNavigate();
  const { getInitials, user } = useAuth();
  const { setField, clearAll, hasOverrides, ...dateValues } = useDateOverride();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const [devPanelOpen, setDevPanelOpen] = useState(false);
  const devPanelRef = useRef<HTMLDivElement>(null);
  const [dateOverrideOpen, setDateOverrideOpen] = useState(false);
  const dateOverrideRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const { data: profile } = useCurrentUserProfile();
  const { data: allRoles } = useUserAccessPlatformRoles(env.enableDevTools && devPanelOpen);
  const { data: allPersonas } = useUserAccessPersonas(env.enableDevTools && devPanelOpen);
  const updateUserMutation = useUpdateUserAccess();

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const currentRoleId = profile?.platformRoles?.[0]?.id ?? 0;
  const currentPersonaId = profile?.persona?.id ?? 0;
  const currentWorkstreamIds = profile?.workstreams?.map((w) => w.id) ?? [];

  const [pendingRoleId, setPendingRoleId] = useState<number>(0);
  const [pendingPersonaId, setPendingPersonaId] = useState<number>(0);

  const handleApply = () => {
    if (!profile?.userId) return;
    setIsSaving(true);
    setSaveError(null);
    updateUserMutation.mutate(
      {
        userId: profile.userId,
        request: {
          platformRoleIds: pendingRoleId ? [pendingRoleId] : [],
          personaId: pendingPersonaId,
          workstreamIds: currentWorkstreamIds,
        },
      },
      {
        onSuccess: () => {
          // Refresh current user profile so route guard + settings card update
          void queryClient.invalidateQueries({ queryKey: userAccessQueryKeys.me });
          // Refetch active panels with the new persona; inactive panels will
          // fetch when next opened.
          void invalidateAiInsightJobs(queryClient);
          setIsSaving(false);
          setDevPanelOpen(false);
        },
        onError: (err) => {
          setSaveError((err as { message?: string })?.message ?? "Failed to save changes.");
          setIsSaving(false);
        },
      }
    );
  };

  const isDirty =
    pendingRoleId !== currentRoleId || pendingPersonaId !== currentPersonaId;

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current?.contains(e.target as Node)) return;
      setProfileOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileOpen]);

  useEffect(() => {
    if (!devPanelOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (devPanelRef.current?.contains(e.target as Node)) return;
      setDevPanelOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [devPanelOpen]);

  useEffect(() => {
    if (!dateOverrideOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dateOverrideRef.current?.contains(e.target as Node)) return;
      setDateOverrideOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dateOverrideOpen]);

  return (
    <header className="app-navbar" role="banner">
      <div className="app-navbar__inner">
        <Link to={AppRoutes.home} className="app-navbar__brand">
          <span className="app-navbar__logo" aria-hidden>
            <img src={logoImage} alt={BrandLabel.LogoAlt} />
          </span>
          <span className="app-navbar__brand-name">{BrandLabel.AppName}</span>
        </Link>

        <div className="app-navbar__utilities">
          {env.enableDevTools && (
            <div className="app-navbar__debug" ref={devPanelRef}>
              <button
                type="button"
                className="app-navbar__icon-btn focus-ring"
                aria-label="Dev: change role and persona"
                title="Dev: change role and persona"
                onClick={() => {
                  if (!devPanelOpen) {
                    setPendingRoleId(currentRoleId);
                    setPendingPersonaId(currentPersonaId);
                  }
                  setDevPanelOpen((open) => !open);
                }}
              >
                <FiTerminal />
              </button>
              {devPanelOpen && (
                <div className="app-navbar__debug-panel">
                  <div className="app-navbar__debug-header">
                    <span>Dev: Role &amp; Persona</span>
                  </div>
                  <div className="app-navbar__date-override-body">
                    <div className="app-navbar__date-override-section-label">Current</div>
                    <div className="app-navbar__debug-current-row">
                      <span className="app-navbar__debug-label">Role:</span>
                      <span className="app-navbar__debug-value">
                        {profile?.platformRoles?.map((r) => r.roleName).join(", ") || "—"}
                      </span>
                    </div>
                    <div className="app-navbar__debug-current-row">
                      <span className="app-navbar__debug-label">Persona:</span>
                      <span className="app-navbar__debug-value">
                        {profile?.persona?.personaName ?? "—"}
                      </span>
                    </div>

                    <div className="app-navbar__date-override-section-label" style={{ marginTop: "0.75rem" }}>
                      Change to
                    </div>

                    <div className="app-navbar__date-override-row">
                      <label className="app-navbar__date-override-label" htmlFor="dev-role-select">
                        Role
                      </label>
                      <select
                        id="dev-role-select"
                        className="app-navbar__date-override-input"
                        value={pendingRoleId || ""}
                        onChange={(e) => setPendingRoleId(Number(e.target.value))}
                      >
                        <option value="">— select —</option>
                        {(allRoles ?? []).map((r) => (
                          <option key={r.id} value={r.id}>{r.roleName}</option>
                        ))}
                      </select>
                    </div>

                    <div className="app-navbar__date-override-row">
                      <label className="app-navbar__date-override-label" htmlFor="dev-persona-select">
                        Persona
                      </label>
                      <select
                        id="dev-persona-select"
                        className="app-navbar__date-override-input"
                        value={pendingPersonaId || ""}
                        onChange={(e) => setPendingPersonaId(Number(e.target.value))}
                      >
                        <option value="">— select —</option>
                        {(allPersonas ?? []).map((p) => (
                          <option key={p.id} value={p.id}>{p.personaName}</option>
                        ))}
                      </select>
                    </div>

                    {saveError && (
                      <p className="app-navbar__debug-error">{saveError}</p>
                    )}

                    <button
                      type="button"
                      className="app-navbar__debug-apply-btn"
                      disabled={!isDirty || isSaving || !profile?.userId}
                      onClick={handleApply}
                    >
                      {isSaving ? "Saving…" : "Apply"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {env.enableDevTools && (
            <div className="app-navbar__date-override" ref={dateOverrideRef}>
              <button
                type="button"
                className={`app-navbar__icon-btn focus-ring${hasOverrides ? " app-navbar__icon-btn--override-active" : ""}`}
                aria-label="Dev: date overrides"
                title="Dev: date overrides"
                onClick={() => setDateOverrideOpen((o) => !o)}
              >
                <FiCalendar />
                {hasOverrides && <span className="app-navbar__override-badge" aria-hidden="true" />}
              </button>
              {dateOverrideOpen && (
                <div className="app-navbar__date-override-panel">
                  <div className="app-navbar__date-override-header">
                    <span>Dev: Date Overrides</span>
                    {hasOverrides && (
                      <button
                        type="button"
                        className="app-navbar__date-override-clear-all"
                        onClick={clearAll}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="app-navbar__date-override-body">
                    <div className="app-navbar__date-override-section-label">Local overrides (dev only)</div>
                    {OVERRIDE_FIELDS.map(({ key, label }) => (
                      <div key={key} className="app-navbar__date-override-row">
                        <label className="app-navbar__date-override-label" htmlFor={`date-override-${key}`}>
                          {label}
                          {dateValues[key] && (
                            <span className="app-navbar__date-override-set" aria-label="overridden"> ●</span>
                          )}
                        </label>
                        <div className="app-navbar__date-override-input-wrap">
                          <input
                            id={`date-override-${key}`}
                            type="date"
                            className="app-navbar__date-override-input"
                            value={dateValues[key]}
                            onChange={(e) => setField(key, e.target.value)}
                          />
                          {dateValues[key] && (
                            <button
                              type="button"
                              className="app-navbar__date-override-clear"
                              aria-label={`Clear ${label}`}
                              onClick={() => setField(key, "")}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="app-navbar__profile" ref={profileRef}>
            <button
              type="button"
              className="app-navbar__avatar-btn focus-ring-inset"
              aria-label={AriaLabel.Profile}
              aria-expanded={profileOpen}
              aria-haspopup="menu"
              onClick={() => {
                if (!profileOpen) {
                  onProfileMenuOpen();
                }
                setProfileOpen((o) => !o);
              }}
            >
              <Avatar label={getInitials()} disabled={false} />
            </button>
            {profileOpen && (
              <div className="app-navbar__settings-menu" role="menu">
                <div className="app-navbar__user-info">
                  <span className="app-navbar__user-name">
                    {user?.displayName || ProfileMenuLabel.SignedInUser}
                  </span>
                  {user?.email && (
                    <span className="app-navbar__user-email">{user.email}</span>
                  )}
                </div>
                <div className="app-navbar__menu-divider" role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  className="app-navbar__settings-item focus-ring"
                  aria-label={AriaLabel.Settings}
                  onClick={() => {
                    setProfileOpen(false);
                    navigate(AppRoutes.settings);
                  }}
                >
                  <span
                    className="Appkit4-icon icon-setting-outline"
                    aria-hidden="true"
                  />
                  <span>{ProfileMenuLabel.Settings}</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="app-navbar__settings-item focus-ring"
                  aria-label={AriaLabel.Logout}
                  onClick={() => {
                    setProfileOpen(false);
                    navigate(AppRoutes.logout);
                  }}
                >
                  <span
                    className="Appkit4-icon icon-logout-outline"
                    aria-hidden="true"
                  />
                  <span>{ProfileMenuLabel.Logout}</span>
                </button>
                <div className="app-navbar__menu-divider" role="separator" />
                <p className="app-navbar__version" role="presentation">
                  v{appVersion}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
